import React, { useState } from "react";
import { ArrowLeft, ArrowRight, History, PencilLine, SkipForward } from "lucide-react";

const ANY = "Not sure / Any";

export function buildRequirements(qset, answers) {
  const list = qset.questions
    .filter((q) => answers[q.id]?.length)
    .map((q) => ({ id: q.id, question: q.question, answer: answers[q.id], is_budget: q.is_budget }));
  return { category: qset.category, answers: list };
}

export function answersFrom(requirements) {
  return Object.fromEntries((requirements?.answers || []).map((a) => [a.id, a.answer]));
}

function OptionChips({ q, selected, onPick }) {
  return <div className="sq-options" data-testid="sq-options">{q.options.map((opt, i) => <button type="button" key={opt} className={`sq-chip ${selected.includes(opt) ? "on" : ""}`} onClick={() => onPick(opt)} data-testid={`sq-option-${i}`}>{opt}</button>)}</div>;
}

export const SmartQuestions = ({ qset, initialAnswers, onComplete, onSkip }) => {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(initialAnswers || {});
  const [other, setOther] = useState({});
  const [otherOpen, setOtherOpen] = useState(false);
  const q = qset.questions[idx];
  const total = qset.questions.length;
  const selected = answers[q.id] || [];
  const meta = (a) => ({ log_id: qset.log_id, total, answered: Object.values(a).filter((v) => v?.length && !(v.length === 1 && v[0] === ANY)).length, skipped: false });
  const finish = (a) => onComplete(buildRequirements(qset, a), meta(a));
  const next = (a = answers) => { setOtherOpen(false); if (idx + 1 < total) setIdx(idx + 1); else finish(a); };
  const pick = (opt) => {
    if (q.type === "multi_select" && opt !== ANY) {
      const cur = selected.filter((x) => x !== ANY);
      setAnswers({ ...answers, [q.id]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] });
      return;
    }
    const a = { ...answers, [q.id]: [opt] };
    setAnswers(a);
    next(a);
  };
  const saveOther = () => { const t = (other[q.id] || "").trim(); if (!t) return; const a = { ...answers, [q.id]: [t] }; setAnswers(a); next(a); };
  return <section className="sq-card" data-testid="smart-questions">
    <div className="sq-top"><span className="sq-progress" data-testid="sq-progress">Question {idx + 1} of {total}</span><div className="sq-bar"><span style={{ width: `${((idx + 1) / total) * 100}%` }} /></div></div>
    {qset.previous_requirements && idx === 0 && <button type="button" className="sq-previous" onClick={() => onComplete(qset.previous_requirements, { ...meta(answersFrom(qset.previous_requirements)), reused: true })} data-testid="sq-use-previous"><History size={14} /> Use my previous answers <small>{qset.previous_requirements.summary}</small></button>}
    <h2 className="sq-question" data-testid="sq-question">{q.question}</h2>
    {q.why_it_matters && <p className="sq-why" data-testid="sq-why">{q.why_it_matters}</p>}
    <OptionChips q={q} selected={selected} onPick={pick} />
    {otherOpen ? <div className="sq-other"><input autoFocus value={other[q.id] || ""} maxLength={60} placeholder="Type what you need" onChange={(e) => setOther({ ...other, [q.id]: e.target.value })} onKeyDown={(e) => e.key === "Enter" && saveOther()} data-testid="sq-other-input" /><button type="button" className="primary-btn" onClick={saveOther} data-testid="sq-other-save">OK</button></div>
      : <button type="button" className="sq-chip ghost" onClick={() => setOtherOpen(true)} data-testid="sq-something-else"><PencilLine size={13} /> Something else</button>}
    <div className="sq-nav">
      {idx > 0 ? <button type="button" className="sq-link" onClick={() => { setOtherOpen(false); setIdx(idx - 1); }} data-testid="sq-back"><ArrowLeft size={14} /> Back</button> : <span />}
      {q.type === "multi_select" && <button type="button" className="primary-btn" onClick={() => next()} disabled={!selected.length} data-testid="sq-next">{idx + 1 < total ? "Next" : "Show results"} <ArrowRight size={14} /></button>}
    </div>
    <button type="button" className="sq-skip" onClick={() => onSkip({ log_id: qset.log_id, total, answered: 0, skipped: true })} data-testid="sq-skip"><SkipForward size={14} /> Skip questions, show me the best options</button>
  </section>;
};
